import { NavigateOptions, useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { setSEOLang } from '@utils/seo';
import { useData } from '@hooks/useData';
import { saveToLocalStorage } from '@utils/storage';

export const useCustomNavigate = () => {
    const location = useLocation();
    const { language: currentLanguage } = useParams();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { allLanguages } = useData();

    // get URL without language
    const getBaseUrl = () => {
        const currentPath = location.pathname;

        if (!currentLanguage) {
            return `${currentPath}${location.search}`;
        }

        const urlSegments = currentPath.split('/');
        return `/${urlSegments.splice(2, urlSegments.length).join('/')}${location.search}`;
    };

    const buildDestinyPath = (targetPath: string) => {
        // Normalize fallback language value to a string
        const fallback = Array.isArray(i18n.options?.fallbackLng)
            ? i18n.options?.fallbackLng[0]
            : typeof i18n.options?.fallbackLng === 'string'
            ? i18n.options?.fallbackLng
            : '';

        // Ensure we work with a pathname + search (ignore origin)
        const rawPath = targetPath || '';

        // Strip any leading supported language segment from the incoming path
        // to avoid duplicating it when we re-build the destination.
        const supported = (i18n.languages as string[]) || [];
        const stripLangPrefix = (path: string) => {
            const match = path.match(/^\/(\w+)(\/|$)/);
            if (match) {
                const candidate = match[1];
                if (supported.includes(candidate)) {
                    // Remove the leading /{lang}
                    return path.replace(new RegExp(`^\/${candidate}`), '') || '/';
                }
            }
            return path;
        };

        const base = stripLangPrefix(rawPath);

        // Default language should not be prefixed in the URL
        if (i18n.language === fallback) {
            return base;
        }

        // Non-default languages should be prefixed
        return base.startsWith(`/${i18n.language}`) ? base : `/${i18n.language}${base}`;
    };

    return {
        navigate: (url: string, options?: NavigateOptions) => {
            navigate(buildDestinyPath(url), options);
        },
        // changes i18n language and updates url
        changeLanguage: (toLanguage: string) => {
            const baseUrl = getBaseUrl();
            i18n.changeLanguage(toLanguage);
            try {
                saveToLocalStorage('language', toLanguage);
            } catch {
            }
            navigate(buildDestinyPath(baseUrl), { replace: true });
            const locale = allLanguages?.data?.find((item) => item.language_code === toLanguage)?.locale_code || toLanguage;
            setSEOLang(locale);
        },
    };
};
